import { AxiosResponse } from 'axios';

import client from 'api/client';
import { ClientsPayload } from 'api/models/payload/ClientsPayload';
import { toCasesQueryCaseId } from 'helpers/dashboardCases';
import { ClientDirectoryUser, SalesClient } from '../models/response';

export interface TransferClientBlocker {
    type: string;
    ids: number[];
    message: string;
}

export interface TransferClientSuccessResponse {
    user_id: number;
    from_service_provider_id: number;
    to_service_provider_id: number;
    applications?: Array<{
        id: number;
        removed_agents: {
            consultant_id: number | null;
            backoffice_id: number | null;
            sales_id: number | null;
        };
        chat_membership_changes: {
            added: number[];
            removed: number[];
        };
    }>;
}

export interface TransferClientAlreadyAssignedResponse {
    msg: string;
    user_id: number;
    service_provider_id: number;
}

export interface TransferClientBlockedResponse {
    msg: string;
    blockers: TransferClientBlocker[];
}

export type ChangeClientServiceProviderResponse =
    | TransferClientSuccessResponse
    | TransferClientAlreadyAssignedResponse;

export function getClientsByPaidOrNotPaid(data: ClientsPayload): Promise<AxiosResponse> {
    const url = `/clients`;
    return client.post(url, data);
}

export function getAllClients(
    candidate_name?: string,
    organization_name?: string
): Promise<AxiosResponse<ClientDirectoryUser[]>> {
    return client.get<ClientDirectoryUser[]>('/clients', {
        params:
            candidate_name || organization_name
                ? {
                      ...(candidate_name ? { candidate_name } : {}),
                      ...(organization_name ? { organization_name } : {}),
                  }
                : undefined,
    });
}

export function get(
    pageNum?: number,
    pageSize?: number,
    client_status?: string,
    payment_status?: string,
    program_name?: string,
    case_id?: string,
    external_id?: string,
    candidate_name?: string,
    candidate_email?: string,
    sort_status_updated_at?: 'asc' | 'desc',
    backoffice_id?: string,
    consultant_id?: string,
    sales_id?: string,
    service_provider_id?: string
): Promise<AxiosResponse<SalesClient[]>> {
    const caseIdParam = toCasesQueryCaseId(case_id);
    const externalIdParam = caseIdParam ? '' : toCasesQueryCaseId(external_id);
    let path = `/cases`;
    if (
        pageNum != null ||
        pageSize != null ||
        client_status != null ||
        payment_status != null ||
        program_name != null ||
        caseIdParam ||
        externalIdParam ||
        candidate_name != null ||
        candidate_email != null ||
        sort_status_updated_at != null
    ) {
        path += `?`;
    }
    if (pageNum != null) {
        path += `page=${pageNum}&`;
    }
    if (pageSize != null) {
        path += `pageSize=${pageSize}&`;
    }
    if (client_status != null) {
        path += `&client_status=${client_status}&`;
    }
    if (payment_status != null) {
        path += `payment_status=${payment_status}&`;
    }
    if (program_name != null) {
        path += `program_name=${program_name}&`;
    }
    if (caseIdParam) {
        path += `case_id=${encodeURIComponent(caseIdParam)}&`;
    } else if (externalIdParam) {
        path += `external_id=${encodeURIComponent(externalIdParam)}&`;
    }
    if (candidate_name != null) {
        path += `candidate_name=${candidate_name}&`;
    }
    if (candidate_email != null) {
        path += `candidate_email=${candidate_email}&`;
    }
    if (sort_status_updated_at != null) {
        path += `sort_status_updated_at=${sort_status_updated_at}&`;
    }
    if (backoffice_id) {
        path += `backoffice_id=${backoffice_id}&`;
    }
    if (consultant_id) {
        path += `consultant_id=${consultant_id}&`;
    }
    if (sales_id) {
        path += `sales_id=${sales_id}&`;
    }
    if (service_provider_id) {
        path += `service_provider_id=${service_provider_id}&`;
    }

    return client.get<SalesClient[]>(path);
}

export function changeClientServiceProvider(
    clientId: number,
    targetServiceProviderId: number
): Promise<AxiosResponse<ChangeClientServiceProviderResponse>> {
    return client.put<ChangeClientServiceProviderResponse>(
        `/clients/${clientId}/change-client-service-provider`,
        { target_service_provider_id: targetServiceProviderId }
    );
}

export interface PromoteToAccountResponse {
    id: number;
    user_kind: 'ACCOUNT' | 'PROFILE_ONLY' | string;
    email?: string | null;
    firstname?: string | null;
    lastname?: string | null;
}

/** Promote a PROFILE_ONLY user to ACCOUNT without sending an invitation email. */
export function promoteToAccount(userId: number): Promise<AxiosResponse<PromoteToAccountResponse>> {
    return client.post<PromoteToAccountResponse>(`/clients/${userId}/promote-to-account`);
}
